import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

const ERROR = {
    status: "error",
    backgroundColour: "#FFBABA",
    color: "#E22B33"
};

const WARNING = {
    status: "warning",
    backgroundColour: "#FEEFB3",
    color: "#AB710F"
};

const INFO = {
    status: "info",
    backgroundColour: "#E3F7FC",
    color: "#5f93a7"
};

const Icon = severity => {
    const props = {
        style: { color: severity.color, marginRight: "10px" },
        fontSize: "small"
    };

    const Component = severity.status === "info" ? WarningRoundedIcon : ErrorRoundedIcon;

    return <Component {...props} aria-hidden="true" />;
};

export function Message({
    className,
    error,
    warning,
    fontSize,
    padding,
    marginTop,
    renderOnPrint,
    text,
    shown,
    ...rest
}) {
    const getSeverity = () => {
        if (error) {
            return ERROR;
        }

        if (warning) {
            return WARNING;
        }

        return INFO;
    };

    let msg = text;
    if (msg instanceof Array) {
        msg = msg.map((m, idx) => (
            <span key={idx}>
                {m}
                <br />
            </span>
        ));
    }

    const severity = getSeverity();

    return (
        <MsgContainer
            marginTop={marginTop}
            shown={shown}
            renderOnPrint={renderOnPrint}
            className={className}
            data-testid={rest["data-testid"] || "message-container"}
        >
            <MsgList padding={padding}>
                <Li severity={severity} fontSize={fontSize}>
                    <Icon />
                    <span>{msg}</span>
                </Li>
            </MsgList>
        </MsgContainer>
    );
}

const Li = styled.li`
    display: flex;
    align-items: center;
    list-style-type: none;
    border: 1px solid;
    border-radius: 10px;
    padding: 10px;
    margin-top: 10px;
    margin-left: 0;
    padding-left: 15px;
    cursor: default;
    text-align: left;
    font-weight: 700;
    font-family: Tahoma, Geneva, Arial, sans-serif;
    font-size: ${props => props.fontSize || "1em"};
    background-color: ${props => props.severity.backgroundColour};
    border-color: ${props => props.severity.color};
    color: ${props => props.severity.color};

    a {
        color: ${props => props.colour} !important;
    }
`;

export const MsgContainer = styled.div`
    margin-left: 0;
    margin-top: ${props => props.marginTop || "0"}px;
    width: 100%;
    float: left;
    clear: left;
    display: ${props => (props.shown ? "initial" : "none")};

    ${props =>
        !props.renderOnPrint &&
        `
        @media print {
            display: none;
        }`}
`;

const MsgList = styled.ul`
    list-style-type: none;
    font-weight: bold;
    margin: 10px 0;
    padding: ${props => props.padding || "10px"};
    border-radius: 4px;
    font-size: 1em;
    margin-top: 5px;
`;

Message.defaultProps = {
    shown: true
};

Message.propTypes = {
    className: PropTypes.string,
    error: PropTypes.bool,
    warning: PropTypes.bool,
    fontSize: PropTypes.string,
    padding: PropTypes.string,
    marginTop: PropTypes.string,
    renderOnPrint: PropTypes.bool,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
    shown: PropTypes.bool
};
